import { memo, useEffect, useRef } from "react";
import { Check, Sparkles } from "lucide-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { IntakeData } from "@/lib/types";

const CELEBRATION_MS = 5000;
const CELEBRATION_MS_REDUCED_MOTION = 1200;

interface SubmitCelebrationCardProps {
  data: Pick<IntakeData, "firstName">;
  onComplete?: () => void;
  autoCompleteMs?: number;
}

export const SubmitCelebrationCard = memo(function SubmitCelebrationCard({
  data,
  onComplete,
  autoCompleteMs,
}: SubmitCelebrationCardProps) {
  const { i18n } = useLingui();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!onCompleteRef.current) return;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ms =
      autoCompleteMs ?? (reduceMotion ? CELEBRATION_MS_REDUCED_MOTION : CELEBRATION_MS);
    const id = window.setTimeout(() => {
      onCompleteRef.current?.();
    }, ms);
    return () => window.clearTimeout(id);
  }, [autoCompleteMs]);

  const name = data.firstName?.trim() ?? "";

  return (
    <div
      className="submit-celebration-root relative w-full overflow-hidden rounded-3xl border border-[#166B93]/10 bg-gradient-to-b from-white via-white to-[#166B93]/[0.06] px-6 py-12 shadow-[0_24px_80px_-24px_rgba(22,107,147,0.35)] sm:px-10 sm:py-14"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={i18n._(msg`Intake submitted successfully`)}
    >
      <div
        className="submit-celebration-blob pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-[#CEF12F]/25 blur-3xl"
        aria-hidden
      />
      <div
        className="submit-celebration-blob-delay pointer-events-none absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-[#166B93]/20 blur-3xl"
        aria-hidden
      />
      <div
        className="submit-celebration-blob pointer-events-none absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FF9142]/15 blur-2xl"
        aria-hidden
      />

      <Sparkles
        className="submit-celebration-sparkle pointer-events-none absolute left-[12%] top-[18%] h-7 w-7 text-[#166B93]/40 sm:left-[14%]"
        strokeWidth={1.5}
        aria-hidden
      />
      <Sparkles
        className="submit-celebration-sparkle-delay pointer-events-none absolute bottom-[22%] right-[10%] h-6 w-6 text-[#CEF12F]/50 sm:right-[12%]"
        strokeWidth={1.5}
        aria-hidden
      />

      <div className="relative mx-auto flex max-w-lg flex-col items-center text-center">
        <div className="relative mb-8 flex h-36 w-36 items-center justify-center sm:h-40 sm:w-40">
          <div
            className="submit-celebration-ring-pulse pointer-events-none absolute inset-0 rounded-full bg-gradient-to-br from-[#166B93]/30 via-[#CEF12F]/20 to-[#FF9142]/25"
            aria-hidden
          />
          <div
            className="submit-celebration-spin-slow pointer-events-none absolute inset-1 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, #166B93, #CEF12F, #FF9142, #166B93)",
              mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              maskComposite: "exclude",
              padding: "3px",
              WebkitMask:
                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
            }}
            aria-hidden
          />
          <div className="relative flex h-[5.25rem] w-[5.25rem] items-center justify-center rounded-full bg-white shadow-[0_12px_40px_-8px_rgba(22,107,147,0.45)] sm:h-[5.75rem] sm:w-[5.75rem]">
            <Check
              className="submit-celebration-check h-12 w-12 text-[#166B93] sm:h-14 sm:w-14"
              strokeWidth={2.5}
              aria-hidden
            />
          </div>
        </div>

        <h1 className="submit-celebration-fade-up font-heading text-[clamp(1.5rem,5vw,2.25rem)] font-bold tracking-tight text-gray-900">
          {name ? (
            <Trans>
              Thank you, <span className="text-[#166B93]">{name}</span>!
            </Trans>
          ) : (
            <Trans>Thank you!</Trans>
          )}
        </h1>

        <p className="submit-celebration-fade-up-delay mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">
          <Trans>
            We have received your information successfully. Our team will review
            it and contact you soon.
          </Trans>
        </p>

        <p className="submit-celebration-fade-up-delay mt-8 text-xs font-medium uppercase tracking-[0.2em] text-[#166B93]/70">
          <Trans>Submission received</Trans>
        </p>
      </div>
    </div>
  );
});
